"use client";
import { EquivalentPathSearchedType } from "@/app/types/equivalent-path";
import { Box, colors, Divider, Drawer, Fade, InputBase, Typography, useTheme } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { p2e } from "@v2/helpers/optimizedHelpers";
import useTouchable from "@v2/hooks/useTouchable";
import api from "@v2/lib/api";
import axios, { AxiosResponse } from "@v2/lib/axios";
import { BlogCategoryType, BlogPostType } from "@v2/types/blog";
import { ArrowRight, FolderOpen, LayoutTemplate, Search, X } from "lucide-react";
import Link from "next/link";
import { useRouter } from "@bprogress/next/app";
import { ChangeEvent, Fragment, KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import MediaV2 from "../MediaV2";
import CenterBox from "../styled/CenterBox";
import TextOverflow from "../styled/TextOverflow";
import { buildItem, trackSearch, trackSelectItem } from "@v2/lib/analytics";
import { SearchBrandItem, SearchProductItem } from "./types";

interface IProps {
    white?: boolean;
    mode?: 'blog';
}

export default function SearchButton({ white, mode }: IProps) {
    const theme = useTheme()
    const timeout = useRef(null)
    const isTouchable = useTouchable()
    const isBlog = mode === 'blog'
    const router = useRouter()

    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    // site search state
    const [products, setProducts] = useState<SearchProductItem[]>([])
    const [brands, setBrands] = useState<SearchBrandItem[]>([])
    const [eqPaths, setEqPaths] = useState<EquivalentPathSearchedType[]>([])

    // blog search state
    const [blogPosts, setBlogPosts] = useState<BlogPostType[]>([])
    const [blogCategories, setBlogCategories] = useState<BlogCategoryType[]>([])

    const closeDrawer = useCallback((e, reason: string) => {
        setOpen(false)
        setProducts([])
        setBrands([])
        setEqPaths([])
        setBlogPosts([])
        setBlogCategories([])
    }, [])

    const openDrawer = useCallback(() => {
        setOpen(true)
    }, [])

    const onChangeInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        setQuery(p2e(e.target.value))
    }, [])

    const onKeyUpInput = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            if (isBlog) {
                router.push('/fa/blog/category' + (query ? `?q=${encodeURIComponent(query)}` : ''));
                setOpen(false);
            } else {
                // Same tab, like every other way into the list: a new tab left the
                // visitor's page behind and opened the results somewhere else.
                router.push('/fa/products?search=' + encodeURIComponent(query));
                setOpen(false);
            }
        }
    }, [query, isBlog, router])

    const search = useCallback(() => {
        if (!query) return;
        setLoading(true)

        if (isBlog) {
            axios.get(api.blog_search + "?query=" + query)
                .then((res: AxiosResponse<{ posts: BlogPostType[], categories: BlogCategoryType[] }>) => {
                    setBlogPosts(res.data.data.posts)
                    setBlogCategories(res.data.data.categories)
                })
                .finally(() => setLoading(false))
        } else {
            axios.get(api.search + "?query=" + query)
                .then((res: AxiosResponse<{
                    brands: SearchBrandItem[],
                    eq_paths: EquivalentPathSearchedType[],
                    product: { hits: SearchProductItem[] }
                }>) => {
                    setBrands(res.data.data.brands)
                    setProducts(res.data.data.product.hits)
                    setEqPaths(res.data.data.eq_paths)
                })
                .finally(() => setLoading(false))
        }
    }, [query, isBlog])

    useEffect(() => {
        clearTimeout(timeout.current)
        timeout.current = setTimeout(() => {
            search()
        }, 600)
    }, [query])

    const hasResults = isBlog
        ? (!!blogPosts.length || !!blogCategories.length)
        : (!!products.length || !!brands.length || !!eqPaths.length)

    return (
        <Fragment>
            <IconButton
                sx={{ height: 42, width: 42 }}
                onClick={openDrawer}
                aria-label={isBlog ? "جستجو در مقالات" : "جستجو در محصولات سایت"}
            >
                <Search color={white ? "#fff" : "#000"} />
            </IconButton>
            <Drawer
                // A suggestion opens in this tab, so the drawer closes as its link is
                // followed instead of staying over the page that loads.
                onClickCapture={(e) => { if ((e.target as HTMLElement).closest('a[href]')) setOpen(false) }}
                ModalProps={{ keepMounted: false }}
                anchor={'top'}
                open={open}
                onClose={closeDrawer}
                disableScrollLock={!isTouchable}
                PaperProps={{
                    sx: {
                        boxShadow: 'unset',
                        [theme.breakpoints.up('sm')]: {
                            mt: '88px',
                            width: 640,
                            mx: 'auto',
                            backgroundColor: 'transparent'
                        }
                    }
                }}
            >
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: '#fff',
                    borderRadius: 1,
                    height: 56,
                    px: 1
                }}>
                    <IconButton sx={{ height: 40, width: 40 }} loading={loading} onClick={search}>
                        {(!loading) && (
                            <Fade in={true}>
                                <CenterBox>
                                    <ArrowRight size={24} />
                                </CenterBox>
                            </Fade>
                        )}
                    </IconButton>
                    <Box sx={{ flex: 1, pl: 1 }}>
                        <InputBase
                            slotProps={{
                                input: {
                                    autoFocus: true,
                                    dir: 'auto',
                                    sx: {
                                        '&::placeholder': {
                                            textAlign: 'left',
                                            color: colors.grey[900]
                                        }
                                    }
                                }
                            }}
                            onKeyUp={onKeyUpInput}
                            onChange={onChangeInput}
                            sx={{ height: 40 }}
                            fullWidth
                            placeholder={isBlog ? "... جستجو در مقالات و دسته‌بندی‌ها" : "... جستجو در محصولات، برندها و"} />
                    </Box>
                    <IconButton sx={{ height: 40, width: 40 }} onClick={(e) => closeDrawer(e, 'closeButton')}>
                        <X size={24} />
                    </IconButton>
                </Box>

                {hasResults && (
                    <Box sx={{
                        mt: 2,
                        borderRadius: 1,
                        bgcolor: '#fff',
                        p: 2,
                        pb: 0,
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        [theme.breakpoints.down('sm')]: {
                            maxHeight: 'calc(100vh - 72px)',
                        }
                    }}>
                        {isBlog ? (
                            <Fragment>
                                {!!blogCategories.length && (
                                    <Fragment>
                                        <Typography sx={{ mb: 1, color: colors.grey[600] }}>دسته‌بندی‌ها</Typography>
                                        {blogCategories.map((cat) => (
                                            <SearchedBlogCategory data={cat} key={cat.id} />
                                        ))}
                                        <Divider sx={{ mb: 2 }} />
                                    </Fragment>
                                )}
                                {!!blogPosts.length && (
                                    <Fragment>
                                        <Typography sx={{ mb: 1, color: colors.grey[600] }}>مقالات</Typography>
                                        {blogPosts.map((post) => (
                                            <SearchedBlogPost data={post} key={post.id} />
                                        ))}
                                    </Fragment>
                                )}
                            </Fragment>
                        ) : (
                            <Fragment>
                                {!!(brands.length) && (
                                    <Fragment>
                                        <Typography sx={{ mb: 1, color: colors.grey[600] }}>برند ها</Typography>
                                        {brands.map((brand) => (
                                            <SearchedBrand data={brand} key={brand.id} />
                                        ))}
                                        <Divider sx={{ mb: 2 }} />
                                    </Fragment>
                                )}

                                {!!(eqPaths.length) && (
                                    <Fragment>
                                        <Typography sx={{ mb: 1, color: colors.grey[600] }}>صفحات مرتبط</Typography>
                                        {eqPaths.map((eqPath) => (
                                            <SearchedEqPath data={eqPath} key={eqPath.id} />
                                        ))}
                                        <Divider sx={{ mb: 2 }} />
                                    </Fragment>
                                )}

                                {!!(products.length) && (
                                    <Fragment>
                                        <Typography sx={{ mb: 1, color: colors.grey[600] }}>محصولات</Typography>
                                        {products.map((sp, spI) => (
                                            <SearchedProduct data={sp} searchTerm={query} index={spI} key={sp.document.id} />
                                        ))}
                                    </Fragment>
                                )}
                            </Fragment>
                        )}
                    </Box>
                )}
            </Drawer>
        </Fragment>
    );
}

const SearchedProduct = ({ data, searchTerm, index }: { data: SearchProductItem, searchTerm?: string, index?: number }) => {
    const splitTitle = data.document.site_product.title.split('-')
    const t1 = splitTitle[0]?.trim()

    // A suggestion that gets clicked is a search that actually went somewhere.
    // Reporting here instead of on every keystroke keeps half typed words out
    // of the search term report.
    const reportClick = () => {
        if (searchTerm) trackSearch(searchTerm)

        trackSelectItem(buildItem({
            variantId: data.document.id,
            name: data.document.name,
            colorCode: data.document.color_code,
            siteProductId: data.document.site_product.id,
            index,
            listId: "search_suggestions",
            listName: "Search suggestions",
        }), "search_suggestions", "Search suggestions")
    }

    return (
        <Box component={Link} href={`/fa/products/${data.document.site_product.slug}?color_code=${data.document.color_code}`} prefetch={false}
            aria-label={data.document.site_product.title}
            onClick={reportClick}
            sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                    '& .search_item_image': {
                        borderColor: 'transparent',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    },
                    '& .search_item_main_title': {
                        color: colors.grey[900]
                    }
                }
            }}>
            <MediaV2 media_id={data.document.image} alt={data.document.name} height={56} width={70} boxProps={{
                className: 'search_item_image',
                sx: {
                    transition: '250ms',
                    width: 78,
                    height: 56,
                    border: '1px solid',
                    borderColor: colors.grey[300],
                    borderRadius: 1,
                }
            }} />

            <Box sx={{
                ml: 2
            }}>
                <Typography className="search_item_main_title" sx={{ transition: '200ms', fontSize: 16, color: colors.grey[700] }}>{t1}</Typography>
                <Typography className="en-num" sx={{ fontSize: 13, mt: 0.5, letterSpacing: 0.5, color: colors.grey[600] }}>{data.document.name}</Typography>
            </Box>
        </Box>
    )
}

const SearchedBrand = ({ data }: { data: SearchBrandItem }) => {
    return (
        <Fade in={true}>
            <Box component={Link} href={`/fa/brand/${data.slug}`} prefetch={false} sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                '&:hover': {
                    '& .search_item_image': {
                        borderColor: 'transparent',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    },
                    '& .search_item_main_title': {
                        color: colors.grey[900]
                    }
                }
            }}>
                <MediaV2 media_id={data?.logo?.id} alt={data.name} height={56} width={70} boxProps={{
                    className: 'search_item_image',
                    sx: {
                        transition: '250ms',
                        width: 78,
                        height: 56,
                        border: '1px solid',
                        borderColor: colors.grey[300],
                        borderRadius: 1,
                    }
                }} />

                <Box sx={{
                    ml: 2
                }}>
                    <Typography className="search_item_main_title" sx={{ transition: '200ms', fontSize: 16, color: colors.grey[700] }}>{data.name}</Typography>
                </Box>
            </Box>
        </Fade>
    )
}

const SearchedBlogPost = ({ data }: { data: BlogPostType }) => {
    const coverMediaId = data.cover?.id ? String(data.cover.id) : null
    return (
        <Box component={Link} href={`/fa/blog/${data.slug}`} prefetch={false}
            aria-label={data.title}
            sx={{
                mb: 2, display: 'flex', alignItems: 'center',
                '&:hover': {
                    '& .search_item_image': { borderColor: 'transparent', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' },
                    '& .search_item_main_title': { color: colors.grey[900] }
                }
            }}>
            <MediaV2 media_id={coverMediaId} alt={data.cover?.alt ?? data.title} height={56} width={70} boxProps={{
                className: 'search_item_image',
                sx: {
                    transition: '250ms', width: 78, height: 56,
                    border: '1px solid', borderColor: colors.grey[300], borderRadius: 1,
                    flexShrink: 0,
                }
            }} />
            <Box sx={{ ml: 2, minWidth: 0 }}>
                <TextOverflow className="search_item_main_title" sx={{ transition: '200ms', fontSize: 15, color: colors.grey[700] }}>
                    {data.title}
                </TextOverflow>
                {data.primary_category && (
                    <Typography sx={{ fontSize: 12, mt: 0.5, color: colors.grey[500] }}>
                        {data.primary_category.name}
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

const SearchedBlogCategory = ({ data }: { data: BlogCategoryType }) => {
    const href = `/fa/blog/category/${data.path_slugs.join('/')}`
    return (
        <Fade in={true}>
            <Box component={Link} href={href} prefetch={false}
                sx={{
                    mb: 2, display: 'flex', alignItems: 'center',
                    '&:hover': {
                        '& .search_item_image': { borderColor: 'transparent', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' },
                        '& .search_item_main_title': { color: colors.grey[900] }
                    }
                }}>
                <CenterBox className="search_item_image" sx={{
                    minWidth: 78, height: 56,
                    bgcolor: colors.grey[100], borderRadius: 1,
                    border: '1px solid', borderColor: colors.grey[200],
                    transition: '250ms',
                }}>
                    <FolderOpen size={22} color={colors.grey[500]} />
                </CenterBox>
                <Box sx={{ ml: 2 }}>
                    <Typography className="search_item_main_title" sx={{ transition: '200ms', fontSize: 15, color: colors.grey[700] }}>
                        {data.name}
                    </Typography>
                </Box>
            </Box>
        </Fade>
    )
}

const SearchedEqPath = ({ data }: { data: EquivalentPathSearchedType }) => {
    return (
        <Fade in={true}>
            <Box component={Link} href={`${data.link}`} prefetch={false} sx={{
                mb: 2,
                width: 1,
                display: 'flex',
                alignItems: 'center',
                '& mark': {
                    backgroundColor: '#FDE5E7',
                    color: 'inherit',
                    padding: '0 4px',
                    borderRadius: '3px',
                },
                '&:hover': {
                    '& .search_item_image': {
                        borderColor: 'transparent',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    },
                    '& .search_item_main_title': {
                        color: colors.grey[900]
                    }
                }
            }}>

                <CenterBox className="search_item_image" sx={{
                    minWidth: 78,
                    height: 56,
                    bgcolor: colors.grey[100],
                    borderRadius: 1,
                }}>
                    <LayoutTemplate />
                </CenterBox>
                <Box sx={{
                    ml: 2,
                    flex: 1,
                    overflow: 'hidden'
                }}>
                    {data.highlight['title']?.snippet ? (

                        <Typography sx={{
                            fontSize: 16,
                            color: colors.grey[700]
                        }} className="search_item_main_title" dangerouslySetInnerHTML={{ __html: data.highlight['title']?.snippet }} />
                    ) : (
                        <Typography className="search_item_main_title"
                            sx={{ fontSize: 16, color: colors.grey[700] }}>{data.title}</Typography>
                    )}

                    {data.highlight['description']?.snippet ? (

                        <TextOverflow sx={{
                            fontSize: 12,
                            color: colors.grey[700],
                            mt: 0.5
                        }} dangerouslySetInnerHTML={{ __html: data.highlight['description']?.snippet }} />
                    ) : (
                        <TextOverflow sx={{
                            fontSize: 12,
                            color: colors.grey[700],
                            mt: 0.5
                        }} >
                            {data.description}
                        </TextOverflow>

                    )}
                </Box>
            </Box>
        </Fade>
    )
}
